import React, { useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Box,
} from '@mui/material';
import { Person, Security, People, Logout, ExpandMore } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getStoredUserData, clearAllAuthData } from '@core/utils/auth';
import { Module } from '@modules/admin/types';
import { usePermission } from '@modules/auth/hooks';

export const UserMenu: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const userData = getStoredUserData();
  const open = Boolean(anchorEl);

  // Check permissions for user management
  const { hasAnyPermission: canAccessUserManagement } = usePermission(Module.USER);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    handleClose();
  };

  const handleLogout = () => {
    clearAllAuthData();
    globalThis.location.href = '/login';
  };

  if (!userData?.user) {
    return null;
  }

  const { user } = userData;

  return (
    <>
      <IconButton
        onClick={handleClick}
        sx={{
          width: 'auto',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1,
          borderRadius: 2,
          '&:hover': {
            bgcolor: 'action.hover',
          },
        }}
      >
        <Avatar
          src={user.userProfileImage || undefined}
          sx={{
            width: 32,
            height: 32,
            bgcolor: 'primary.main',
          }}
        >
          {user.name.charAt(0).toUpperCase()}
        </Avatar>
        <ExpandMore fontSize="small" />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 220,
              mt: 1,
            },
          },
        }}
      >
        {/* User Info Header */}
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {user.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {user.email}
          </Typography>
        </Box>

        <Divider />

        {/* Menu Items */}
        <MenuItem onClick={() => handleNavigate('/profile')}>
          <ListItemIcon>
            <Person fontSize="small" />
          </ListItemIcon>
          <ListItemText>Profile</ListItemText>
        </MenuItem>

        <Divider sx={{ my: 1 }} />

        {canAccessUserManagement && (
          <MenuItem onClick={() => handleNavigate('/admin/users')}>
            <ListItemIcon>
              <People fontSize="small" />
            </ListItemIcon>
            <ListItemText>User Management</ListItemText>
          </MenuItem>
        )}

        {canAccessUserManagement && (
          <MenuItem onClick={() => handleNavigate('/admin/roles')}>
            <ListItemIcon>
              <Security fontSize="small" />
            </ListItemIcon>
            <ListItemText>Role Management</ListItemText>
          </MenuItem>
        )}

        <Divider sx={{ my: 1 }} />

        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export default UserMenu;
